import { supabase } from '../main.js';
import { icon } from '../utils/icons.js';
import { footerElement } from '../components/Footer.js'
import { projects as workProjects } from '../content/work.js';

function esc(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function safeUrl(url) {
  if (!url) return '';
  try {
    const u = new URL(url, location.origin);
    return (u.protocol === 'https:' || u.protocol === 'http:') ? url : '';
  } catch { return ''; }
}

export async function renderProjectsPage(container) {
  const header = document.createElement('section');
  header.className = 'rm-page';
  header.innerHTML = [
    '<div class="rm-container">',
    '<div class="rm-page-header">',
    '<p class="rm-eyebrow">Work</p>',
    '<h1 class="rm-page-title">Case Studies</h1>',
    '<p class="rm-page-description">The problems we solved, how we built it, and what it delivered.</p>',
    '</div></div></section>'
  ].join('');
  container.replaceChildren(header);

  try {
    let projects = [];

    if (supabase) {
      const timeout = new Promise(res => setTimeout(() => res({ data: null, error: 'timeout' }), 4000));
      const query = supabase.from('portfolio_projects').select('*').order('created_at', { ascending: false });
      const { data, error } = await Promise.race([query, timeout]);
      if (!error && data) projects = data;
    }

    if (!projects || projects.length === 0) projects = getMockProjects();
    renderProjects(container, projects);
  } catch (err) {
    console.error('Error rendering projects page:', err);
    renderProjects(container, getMockProjects());
  }
}

function getMockProjects() {
  return workProjects;
}

function normalizeProject(project, index) {
  let technologies = [];
  if (Array.isArray(project.technologies)) {
    technologies = project.technologies;
  } else if (typeof project.technologies === 'string') {
    try { technologies = JSON.parse(project.technologies); }
    catch { technologies = project.technologies.split(',').map(t => t.trim()); }
  }
  return {
    ...project,
    technologies,
    num: String(index + 1).padStart(2, '0'),
    category: project.category || 'Project',
    year: project.year || new Date(project.created_at || Date.now()).getFullYear()
  };
}

function buildCard(project) {
  const card = document.createElement('a');
  card.href = `/projects/${esc(project.slug)}`;
  card.className = 'cs-card';

  // Image area
  const imageDiv = document.createElement('div');
  imageDiv.className = 'cs-card-image';
  const imgUrl = safeUrl(project.image_url);
  if (imgUrl) {
    imageDiv.style.cssText = `background-image:url('${imgUrl}');background-size:cover;background-position:center top;`;
  }

  const numSpan = document.createElement('span');
  numSpan.className = 'cs-card-num';
  numSpan.textContent = project.num;

  const catSpan = document.createElement('span');
  catSpan.className = 'cs-card-category';
  catSpan.textContent = project.category;

  imageDiv.append(numSpan, catSpan);

  // Body
  const body = document.createElement('div');
  body.className = 'cs-card-body';

  const meta = document.createElement('div');
  meta.className = 'cs-card-meta';
  const yearSpan = document.createElement('span');
  yearSpan.className = 'cs-card-year';
  yearSpan.textContent = project.year;
  meta.appendChild(yearSpan);

  const title = document.createElement('h2');
  title.className = 'cs-card-title';
  title.textContent = project.title;

  const problem = document.createElement('p');
  problem.className = 'cs-card-problem';
  problem.textContent = project.problem || project.description;

  const techRow = document.createElement('div');
  techRow.className = 'cs-card-tech';
  project.technologies.slice(0, 4).forEach(t => {
    const pill = document.createElement('span');
    pill.className = 'rm-tech-tag';
    pill.textContent = t;
    techRow.appendChild(pill);
  });

  const readLink = document.createElement('span');
  readLink.className = 'cs-read-link';
  readLink.textContent = 'Read case study ';
  const arrowSpan = document.createElement('span');
  arrowSpan.className = 'cs-read-arrow';
  arrowSpan.innerHTML = icon('arrow-right', { size: 14 });
  readLink.appendChild(arrowSpan);

  body.append(meta, title, problem, techRow, readLink);
  card.append(imageDiv, body);
  return card;
}

function renderProjects(container, rawProjects) {
  const projects = rawProjects.map(normalizeProject);

  const section = document.createElement('section');
  section.className = 'rm-page';

  const inner = document.createElement('div');
  inner.className = 'rm-container';

  const pageHeader = document.createElement('div');
  pageHeader.className = 'rm-page-header';
  pageHeader.innerHTML = [
    '<p class="rm-eyebrow">Work</p>',
    '<h1 class="rm-page-title">Case Studies</h1>',
    '<p class="rm-page-description">The problems we solved, how we built it, and what it delivered.</p>'
  ].join('');

  const grid = document.createElement('div');
  grid.className = 'cs-grid';

  if (projects.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'rm-empty-message';
    empty.textContent = 'No case studies published yet.';
    grid.appendChild(empty);
  } else {
    projects.forEach(p => grid.appendChild(buildCard(p)));
  }

  inner.append(pageHeader, grid);
  section.appendChild(inner);

  const footer = footerElement();

  container.replaceChildren(section, footer);
}
